import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { auth as authApi } from '../services/api';

function ResetPassword() {
  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  }, []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  function handlePasswordChange(value) {
    setPassword(value);
    let strength = 0;
    if (value.length >= 8) strength++;
    if (/[A-Z]/.test(value)) strength++;
    if (/[0-9]/.test(value)) strength++;
    if (/[^A-Za-z0-9]/.test(value)) strength++;
    setPasswordStrength(strength);
  }

  function getBarClass(index) {
    if (index >= passwordStrength) return 'ps-bar';
    const cls = passwordStrength <= 1 ? 'weak' : passwordStrength <= 2 ? 'medium' : 'strong';
    return `ps-bar ${cls}`;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!token) {
      setError('Link inválido ou expirado.');
      return;
    }

    if (password.length < 6) {
      setError('Mínimo 6 caracteres.');
      return;
    }

    if (password !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await authApi.resetPassword(token, password);

      if (data?.ok) {
        setSuccess(true);
      } else {
        setError(data?.error || 'Erro ao redefinir senha. O link pode ter expirado.');
      }
    } catch (apiError) {
      setError(apiError.message || 'Erro ao redefinir senha. O link pode ter expirado.');
    }

    setLoading(false);
  }

  if (!token) {
    return (
      <div className="page">
        <div className="left">
          <Link to="/" className="left-logo">
            <img src={require('../logo.png')} alt="PokerMind Brasil" style={{ height: '46px', objectFit: 'contain' }} />
          </Link>
          <div className="left-copy">
            <div className="left-tag">Nova senha</div>
            <h2 className="left-title">Defina uma senha segura para<br /><span className="grad">continuar sua jornada.</span></h2>
          </div>
        </div>
        <div className="right">
          <div className="form-wrap">
            <div className="alert-error visible" style={{ marginBottom: '18px' }}>Link inválido ou expirado.</div>
            <Link to="/login" className="btn-submit" style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}>
              Ir para o login →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="left">
        <Link to="/" className="left-logo">
          <img src={require('../logo.png')} alt="PokerMind Brasil" style={{ height: '46px', objectFit: 'contain' }} />
        </Link>

        <div className="left-copy">
          <p className="left-tag">Nova senha</p>
          <h2 className="left-title">Defina uma senha segura para<br /><span className="grad">continuar sua jornada no PokerMind.</span></h2>
        </div>
      </div>

      <div className="right">
        <div className="form-wrap">
          <div className="form-header">
            <h1>Criar nova senha</h1>
            <p>Escolha uma senha forte para proteger sua conta.</p>
          </div>

          {success ? (
            <>
              <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: '10px', padding: '14px', fontSize: '0.84rem', color: 'var(--green)', lineHeight: 1.6, marginBottom: '20px' }}>
                Senha redefinida com sucesso. Agora você já pode entrar com a nova senha.
              </div>
              <Link to="/login" className="btn-submit" style={{ textDecoration: 'none', display: 'inline-flex', justifyContent: 'center' }}>
                Ir para o login →
              </Link>
            </>
          ) : (
            <form noValidate onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="reset-password">Nova senha</label>
                <div className="input-icon-wrap">
                  <input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Mínimo 6 caracteres"
                    value={password}
                    onChange={(event) => handlePasswordChange(event.target.value)}
                  />
                  <button
                    type="button"
                    className="input-icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{ color: showPassword ? 'var(--yellow)' : 'var(--muted)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="password-strength">
                <div className="ps-bars">
                  {[0, 1, 2, 3].map((index) => (
                    <span key={index} className={getBarClass(index)} />
                  ))}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '18px' }}>
                <label htmlFor="reset-confirm-password">Confirmar senha</label>
                <div className="input-icon-wrap">
                  <input
                    id="reset-confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Repita a senha"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                  />
                  <button
                    type="button"
                    className="input-icon"
                    onClick={() => setShowConfirm((prev) => !prev)}
                    style={{ color: showConfirm ? 'var(--yellow)' : 'var(--muted)' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>

              {error && <div className="alert-error visible" style={{ marginTop: '10px', marginBottom: '14px' }}>{error}</div>}

              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar nova senha'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
